var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { CustomRedirect } from '@/components/functional/Link/CustomRedirect';
import Layout from '@/container/Layout';
import { useHotlineClientDetailAccept } from '@/feature/hotline/client/detail/hook/useHotlineClientDetailAccept';
import { useHotlineClientDetailAdvisor } from '@/feature/hotline/client/detail/hook/useHotlineClientDetailAdvisor';
import { useHotlineClientDetailFetch } from '@/feature/hotline/client/detail/hook/useHotlineClientDetailFetch';
import { useHotlineClientDetailNegotiateMessage } from '@/feature/hotline/client/detail/hook/useHotlineClientDetailNegotiateMessage';
import { useHotlineClientBulkCancel } from '@/feature/hotline/client/detail/negotiate/hook/useHotlineClientBulkCancel';
import { useHotlineClientDetailClip } from '@/feature/hotline/client/detail/negotiate/hook/useHotlineClientDetailClip';
import { useHotlineClientNegotiateFile } from '@/feature/hotline/client/detail/negotiate/hook/useHotlineClientNegotiateFile';
import { HotlineClientDetailNegotiateView } from '@/feature/hotline/client/detail/negotiate/page/HotlineClientDetailNegotiateView';
import { HotlineClientDetailNegotiateViewSkeleton } from '@/feature/hotline/client/detail/negotiate/page/HotlineClientDetailNegotiateViewSkeleton';
import { useHotlineClientPause } from '@/feature/hotline/client/hook/useHotlineClientPause';
import { useHotlineRequestInterviewCancelClient } from '@/feature/hotline/client/hook/useHotlineRequestInterviewCancelClient';
import { useHotlineRequestInterviewRescheduleClient } from '@/feature/hotline/client/hook/useHotlineRequestInterviewRescheduleClient';
import { useHotlineRequestInterviewScheduleClient } from '@/feature/hotline/client/hook/useHotlineRequestInterviewScheduleClient';
import { HotlineMeta } from '@/feature/hotline/component/HotlineMeta';
import { useResourceUpload } from '@/hooks/file/useResource';
import { useResourceFromInput } from '@/hooks/file/useResourceView';
import { useLocation } from '@/hooks/lib/react-router/useLocation';
import { useParams } from '@/hooks/lib/react-router/useParams';
import { useUnmountRef } from '@/hooks/lifecycle/useUnmountRef';
import { useTypeSelector } from '@/hooks/state/useTypeSelector';
import { parseQuery, parsedQueryToString, } from '@/utils/libs/query-string/parse';
import { pagePath } from '@/utils/router/pagePath';
/**
 * 匿名コンサルホットライン クライアント 相談画面（ロジック部分のみ）
 */
export const HotlineClientDetailNegotiatePage = () => {
    const { id = '' } = useParams();
    // 初期表示するリクエストIDを取得
    const { search } = useLocation();
    const queryParams = parseQuery(search);
    const paramRequestIdString = parsedQueryToString(queryParams.requestId);
    const paramRequestIdNumber = parseInt(paramRequestIdString, 10);
    const initialRequestId = Number.isNaN(paramRequestIdNumber)
        ? null
        : paramRequestIdNumber;
    const profile = useTypeSelector((state) => state.profile);
    const dispatch = useDispatch();
    const unmountRef = useUnmountRef();
    // 詳細データ
    const { detailData, fetchDetailData, isLoading, setDetailData } = useHotlineClientDetailFetch({
        dispatch,
        id,
        unmountRef,
    });
    const detailOneData = detailData[id];
    // 詳細データ取得
    useEffect(() => {
        ;
        (() => __awaiter(void 0, void 0, void 0, function* () {
            if (!detailOneData) {
                yield fetchDetailData();
            }
        }))();
    }, [detailOneData, fetchDetailData]);
    /**
     * 既読ログ更新後のイベント
     */
    const onSuccessSubmitLog = useCallback((requestId) => {
        if (!detailOneData) {
            return;
        }
        // stateを更新して画面上で既読状態にする（既読更新はコア機能に影響がない処理であり、かつ短期間で何度もトリガーされる可能性がある観点から、apiでデータの引き直し等はあえて行っていない）
        setDetailData((prevState) => (Object.assign(Object.assign({}, prevState), { [id]: Object.assign(Object.assign({}, detailOneData), { hotline_requests: detailOneData.hotline_requests.map((hotlineRequest) => {
                    if (hotlineRequest.id !== requestId) {
                        return hotlineRequest;
                    }
                    return Object.assign(Object.assign({}, hotlineRequest), { messages: hotlineRequest.messages.map((message) => (Object.assign(Object.assign({}, message), { is_unread: false }))), unread_count: 0 });
                }) }) })));
    }, [detailOneData, id, setDetailData]);
    /**
     * メッセージ送信処理
     */
    const { displayLastMessage, onSubmitMessage } = useHotlineClientDetailNegotiateMessage({
        dispatch,
        id,
        onSuccessSubmitLog,
        // メッセージ送信後はデータを引き直して画面を再描画する（送信後は常に最新の状態を保つため）
        onSuccessSubmitMessage: fetchDetailData,
        unmountRef,
    });
    /**
     * 日程承認 送信処理
     */
    const { onSubmitAccept } = useHotlineClientDetailAccept({
        dispatch,
        id,
        onSuccess: fetchDetailData,
    });
    /**
     * アドバイザーへの相談 送信処理
     */
    const { onSubmitAdviserModal } = useHotlineClientDetailAdvisor({
        dispatch,
        id,
        onSuccess: fetchDetailData,
    });
    /**
     * キャンセル申請 送信処理
     */
    const { onSubmitCancel } = useHotlineRequestInterviewCancelClient({
        dispatch,
        id,
        onSubmitCallback: fetchDetailData,
    });
    /**
     * 日程申請 送信処理
     */
    const { onSubmitSchedule } = useHotlineRequestInterviewScheduleClient({
        dispatch,
        id,
        onSubmitCallback: fetchDetailData,
    });
    /**
     * 日程変更申請 送信処理
     */
    const { onSubmitScheduleChange } = useHotlineRequestInterviewRescheduleClient({
        dispatch,
        id,
        onSubmitCallback: fetchDetailData,
    });
    /**
     * 新規オファー停止処理
     */
    const { onSubmitPauseOffer } = useHotlineClientPause({
        dispatch,
        id,
        onSubmitCallback: fetchDetailData,
    });
    /**
     * ファイルDL処理
     */
    const { onClickFileLink } = useHotlineClientNegotiateFile({
        dispatch,
        unmountRef,
    });
    /**
     * ファイルアップロード処理
     */
    const { inspectFileStatus, isUploading, uploadFileToStorage } = useResourceUpload({
        dispatch,
        unmountRef,
    });
    /**
     * アップロードファイル Form 設定処理
     */
    const { inputUploadFileEvent: uploadFile } = useResourceFromInput({
        fileType: 'object',
        uploadFileToStorage,
    });
    /**
     * 一覧のクリップ 状態管理
     */
    const { requestClipList, updateClipState } = useHotlineClientDetailClip({
        id,
    });
    /**
     * 一括キャンセル処理
     */
    const { cancelTargetIds, onSubmitBulkCancel, updateCancelTargetIds } = useHotlineClientBulkCancel({
        dispatch,
        id,
        onSubmitCallback: fetchDetailData,
        unmountRef,
    });
    // ローディング中
    if (isLoading && !detailOneData) {
        return (_jsx(Layout, Object.assign({ mainClassName: 'l-main' }, { children: _jsx(HotlineClientDetailNegotiateViewSkeleton, {}) })));
    }
    // データが取得できなかった場合、相談一覧にリダイレクト
    if (!detailOneData) {
        return _jsx(CustomRedirect, { to: pagePath.mypage.client.hotline() });
    }
    return (_jsxs(_Fragment, { children: [_jsx(HotlineMeta, { relativePath: pagePath.hotline.client.detailNegotiate(id), title: `${detailOneData.title} チャット | PROFFIT` }), _jsx(Layout, Object.assign({ mainClassName: 'l-main' }, { children: _jsx(HotlineClientDetailNegotiateView, { cancelTargetIds: cancelTargetIds, detailData: detailOneData, displayLastMessage: displayLastMessage, initialSelectedId: initialRequestId, isUploading: isUploading, onClickFileLink: onClickFileLink, onInspectFileStatus: inspectFileStatus, onSubmitAccept: onSubmitAccept, onSubmitAdviserModal: onSubmitAdviserModal, onSubmitBulkCancel: onSubmitBulkCancel, onSubmitCancel: onSubmitCancel, onSubmitMessage: onSubmitMessage, onSubmitPauseOffer: onSubmitPauseOffer, onSubmitSchedule: onSubmitSchedule, onSubmitScheduleChange: onSubmitScheduleChange, profile: profile, requestClipList: requestClipList, updateCancelTargetIds: updateCancelTargetIds, updateClipState: updateClipState, uploadFile: uploadFile }) }))] }));
};
